// frontend/src/pages/admin/AdminAutoAssignPage.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../api/axios';
import { Loader2, PlayCircle, AlertCircle } from 'lucide-react';

interface Doctor {
    id: number;
    name: string;
    email: string | null;
    zoom_email: string | null;
    assignment_priority: number;
}

interface AutoAssignResult {
    reservation_id: number;
    user_email: string | null;
    user_name: string | null;
    service: string | null;
    date: string | null;
    time: string | null;
    assigned_doctor_id: number | null;
    assigned_doctor_name: string | null;
    status: string;
}

const AdminAutoAssignPage: React.FC = () => {
    const { t } = useTranslation();

    const [doctors, setDoctors] = useState<Doctor[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [running, setRunning] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [withinMinutes, setWithinMinutes] = useState(60);
    const [results, setResults] = useState<AutoAssignResult[]>([]);

    // Fetch doctors
    useEffect(() => {
        fetchDoctors();
    }, []);

    const fetchDoctors = async () => {
        try {
            setLoading(true);
            const res = await axios.get('/admin/doctors', { params: { limit: 100 } });
            setDoctors(res.data);
        } catch (error) {
            console.error('Failed to fetch doctors:', error);
            setMessage({ type: 'error', text: t('admin.autoAssign.fetchDoctorsError') });
        } finally {
            setLoading(false);
        }
    };

    const handlePriorityChange = (doctorId: number, value: string) => {
        const numValue = parseInt(value) || 9999;
        setDoctors(doctors.map(d => d.id === doctorId ? { ...d, assignment_priority: numValue } : d));
    };

    const handleSavePriorities = async () => {
        try {
            setSaving(true);
            setMessage(null);

            // Update each doctor's priority
            for (const doctor of doctors) {
                await axios.put(`/admin/users/${doctor.id}`, {
                    assignment_priority: doctor.assignment_priority
                });
            }

            setMessage({ type: 'success', text: t('admin.autoAssign.savePrioritiesSuccess') });
        } catch (error) {
            console.error('Failed to save priorities:', error);
            setMessage({ type: 'error', text: t('admin.autoAssign.savePrioritiesError') });
        } finally {
            setSaving(false);
        }
    };

    const handleRunAutoAssign = async () => {
        try {
            setRunning(true);
            setMessage(null);
            setResults([]);

            const res = await axios.post('/admin/reservations/auto_assign_upcoming', null, {
                params: { within_minutes: withinMinutes }
            });

            setResults(res.data);

            const assignedCount = res.data.filter((r: AutoAssignResult) => r.status === 'assigned').length;
            const failedCount = res.data.filter((r: AutoAssignResult) => r.status === 'no_doctor_assigned').length;

            if (failedCount > 0) {
                setMessage({
                    type: 'error',
                    text: t('admin.autoAssign.runPartialSuccess', { assigned: assignedCount, failed: failedCount })
                });
            } else {
                setMessage({
                    type: 'success',
                    text: t('admin.autoAssign.runSuccess', { count: assignedCount })
                });
            }
        } catch (error) {
            console.error('Failed to run auto-assign:', error);
            setMessage({ type: 'error', text: t('admin.autoAssign.runError') });
        } finally {
            setRunning(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">{t('admin.autoAssign.title')}</h1>
                    <p className="mt-2 text-gray-600">{t('admin.autoAssign.description')}</p>
                </div>

                {/* Message */}
                {message && (
                    <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                        <div className="flex items-center gap-2">
                            <AlertCircle className="w-5 h-5" />
                            <span>{message.text}</span>
                        </div>
                    </div>
                )}

                {/* Run Auto-Assign Section */}
                <div className="bg-white rounded-lg shadow p-6 mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('admin.autoAssign.runSection')}</h2>

                    <div className="flex items-end gap-4 mb-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('admin.autoAssign.withinMinutes')}
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="1440"
                                value={withinMinutes}
                                onChange={(e) => setWithinMinutes(parseInt(e.target.value) || 60)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>
                        <button
                            onClick={handleRunAutoAssign}
                            disabled={running}
                            className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {running ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <PlayCircle className="w-5 h-5" />
                            )}
                            {t('admin.autoAssign.runButton')}
                        </button>
                    </div>

                    {/* Results */}
                    {results.length > 0 && (
                        <div className="mt-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-3">{t('admin.autoAssign.results')}</h3>

                            {/* ▼ モバイル: カード表示（横スクロールバーを出さずに縦積み） */}
                            <div className="md:hidden divide-y divide-gray-200">
                                {results.map((result) => (
                                    <div key={result.reservation_id} className="py-4">
                                        {/* 予約ID + ステータス */}
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="text-xs text-gray-400">{t('admin.autoAssign.reservationId')}</div>
                                                <div className="font-semibold text-gray-900 break-words">#{result.reservation_id}</div>
                                            </div>
                                            <span className={`shrink-0 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${result.status === 'assigned' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {result.status === 'assigned' ? t('admin.autoAssign.assigned') : t('admin.autoAssign.failed')}
                                            </span>
                                        </div>

                                        {/* 各項目（ラベル：値） */}
                                        <div className="mt-3 space-y-1 text-sm">
                                            <div className="break-words">
                                                <span className="text-gray-400">{t('admin.autoAssign.patient')}：</span>
                                                <span className="text-gray-900">{result.user_name || result.user_email || 'N/A'}</span>
                                            </div>
                                            <div className="break-words">
                                                <span className="text-gray-400">{t('admin.autoAssign.service')}：</span>
                                                <span className="text-gray-900">{result.service || 'N/A'}</span>
                                            </div>
                                            <div className="break-words">
                                                <span className="text-gray-400">{t('admin.autoAssign.datetime')}：</span>
                                                <span className="text-gray-900">{result.date} {result.time}</span>
                                            </div>
                                            <div className="break-words">
                                                <span className="text-gray-400">{t('admin.autoAssign.doctor')}：</span>
                                                <span className="text-gray-900">{result.assigned_doctor_name || '-'}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* ▼ デスクトップ: テーブル表示 */}
                            <div className="overflow-x-auto">
                                <table className="hidden md:table min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.autoAssign.reservationId')}</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.autoAssign.patient')}</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.autoAssign.service')}</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.autoAssign.datetime')}</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.autoAssign.doctor')}</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.autoAssign.status')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {results.map((result) => (
                                            <tr key={result.reservation_id}>
                                                <td className="px-4 py-3 text-sm text-gray-900">{result.reservation_id}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900">{result.user_name || result.user_email || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900">{result.service || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900">{result.date} {result.time}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900">{result.assigned_doctor_name || '-'}</td>
                                                <td className="px-4 py-3 text-sm">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${result.status === 'assigned' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                        }`}>
                                                        {result.status === 'assigned' ? t('admin.autoAssign.assigned') : t('admin.autoAssign.failed')}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Doctor Priority Section */}
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold text-gray-900">{t('admin.autoAssign.prioritySection')}</h2>
                        <button
                            onClick={handleSavePriorities}
                            disabled={saving}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? (
                                <span className="flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    {t('admin.autoAssign.saving')}
                                </span>
                            ) : (
                                t('admin.autoAssign.savePriorities')
                            )}
                        </button>
                    </div>

                    {/* ▼ モバイル: カード表示（横スクロールバーを出さずに縦積み） */}
                    <div className="md:hidden divide-y divide-gray-200">
                        {doctors.map((doctor) => (
                            <div key={doctor.id} className="py-4">
                                {/* 医師名 + 優先度入力 */}
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="font-medium text-gray-900 break-words">{doctor.name}</div>
                                    </div>
                                    <div className="shrink-0 flex items-center gap-2">
                                        <span className="text-xs text-gray-400">{t('admin.autoAssign.priority')}</span>
                                        <input
                                            type="number"
                                            min="1"
                                            max="9999"
                                            value={doctor.assignment_priority}
                                            onChange={(e) => handlePriorityChange(doctor.id, e.target.value)}
                                            className="w-20 px-2 py-1 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                        />
                                    </div>
                                </div>

                                {/* メール / Zoom メール（ラベル：値） */}
                                <div className="mt-3 space-y-1 text-sm">
                                    <div className="break-all">
                                        <span className="text-gray-400">{t('admin.autoAssign.email')}：</span>
                                        <span className="text-gray-500">{doctor.email || '-'}</span>
                                    </div>
                                    <div className="break-all">
                                        <span className="text-gray-400">{t('admin.autoAssign.zoomEmail')}：</span>
                                        <span className="text-gray-500">{doctor.zoom_email || '-'}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ▼ デスクトップ: テーブル表示 */}
                    <div className="overflow-x-auto">
                        <table className="hidden md:table min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.autoAssign.doctorName')}</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.autoAssign.email')}</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.autoAssign.zoomEmail')}</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.autoAssign.priority')}</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {doctors.map((doctor) => (
                                    <tr key={doctor.id}>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{doctor.name}</td>
                                        <td className="px-4 py-3 text-sm text-gray-500">{doctor.email || '-'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-500">{doctor.zoom_email || '-'}</td>
                                        <td className="px-4 py-3">
                                            <input
                                                type="number"
                                                min="1"
                                                max="9999"
                                                value={doctor.assignment_priority}
                                                onChange={(e) => handlePriorityChange(doctor.id, e.target.value)}
                                                className="w-24 px-2 py-1 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-4 text-sm text-gray-600">
                        <p>{t('admin.autoAssign.priorityNote')}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminAutoAssignPage;
